<template>
    <section class="section">
        <div class="container">
            <div class="columns">
                <div class="column is-12">
                    <h1 class="title">{{ translate("admin_results.title") }}</h1>
                </div>
            </div>

            <div v-if="!initDone" class="columns is-centered">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
            <template v-else>
                <div class="columns">
                    <div class="column is-12">
                        <div class="buttons">
                            <button
                                v-if="sharingEnabled"
                                class="button is-danger"
                                :disabled="!canManageResults"
                                @click="changeSharingStatus"
                            >
                                <span class="icon">
                                    <i class="fa fa-times"></i>
                                </span>
                                <span>
                                    {{ translate("common.dont_share") }}
                                </span>
                            </button>
                            <button v-else class="button is-success" :disabled="!canManageResults" @click="changeSharingStatus">
                                <span class="icon">
                                    <i class="fa fa-share" />
                                </span>
                                <span>
                                    {{ translate("common.share") }}
                                </span>
                            </button>

                            <button class="button" @click="refreshResults">
                                <span class="icon">
                                    <i class="fa fa-sync-alt" />
                                </span>
                                <span>
                                    {{ translate("common.refresh") }}
                                </span>
                            </button>

                            <a class="button" :href="getUrlToDownloadCsv()">
                                <span class="icon">
                                    <i class="fa fa-download" />
                                </span>
                                <span>
                                    {{ translate("common.download_in_csv") }}
                                </span>
                            </a>
                        </div>

                        <CompetitorResultsOptionsOverview
                            :event-id="eventId"
                            ref="competitorResultsOptionsOverview"
                            :can-change-options="canManageResults"
                            @changed="refreshResults"
                        />
                    </div>
                </div>

                <div class="columns">
                    <div class="column is-12">
                        <div class="mb-0 is-flex is-align-items-center is-flex-wrap-wrap">
                            <QueryInput v-model="searchQuery" class="mr-2" />
                            <div class="mb-3">
                                <Checkbox2 v-model="showDecorated" @change="onShowDecoratedChange">
                                    {{ translate("admin_results.show_decorated") }} ({{ totalDecorated }}/{{ totalCategories }})
                                </Checkbox2>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="columns is-centered" v-if="requestsInProgress > 0">
                    <LoadingSpinner2 class="has-added-margin" />
                </div>
                <template v-else-if="filteredCategoryPodiums.length > 0">
                    <div
                        class="category-container"
                        v-for="(categoryPodium, index) in filteredCategoryPodiums"
                        :key="categoryPodium.parameter_id"
                    >
                        <div class="columns is-multiline">
                            <div class="column is-12 is-flex evenly-distributed mb-0 pb-0">
                                <div class="category-name title is-5" :id="index === 0 ? 'competitor-results-first-category' : undefined">
                                    <span v-if="categoryPodium.bracketAvailable">
                                        <a href="#" @click.prevent="openBracketSidebar(categoryPodium.parameter_id)">
                                            {{ categoryPodium.category }}
                                        </a>
                                        <BracketTypeBadge :fight="categoryPodium" />
                                    </span>

                                    <span v-else>{{ categoryPodium.category }}</span>
                                </div>
                                <div class="finish-time">
                                    {{ convertToTimezone(categoryPodium.categoryFinishTime, eventTimezone) }}
                                </div>

                                <button
                                    v-show="!showDecorated"
                                    @click="saveDecoration(categoryPodium.parameter_id)"
                                    class="button is-small"
                                >
                                    <span class="icon">
                                        <i class="fa fa-check" />
                                    </span>
                                    <span>
                                        {{ translate("admin_results.mark_as_decorated") }}
                                    </span>
                                </button>
                                <button
                                    v-show="showDecorated"
                                    @click="removeDecoration(categoryPodium.parameter_id)"
                                    class="button is-small"
                                >
                                    <span class="icon">
                                        <i class="fa fa-undo" />
                                    </span>
                                    <span>
                                        {{ translate("common.undo") }}
                                    </span>
                                </button>
                            </div>

                            <div class="column is-12">
                                <KataResultsTable
                                    v-if="categoryPodium.bracketType === BracketTypeEnum.Kata"
                                    :category-podium="categoryPodium"
                                />
                                <StandardResultsTable v-else :category-podium="categoryPodium" />
                            </div>
                        </div>
                    </div>
                </template>
                <div class="columns is-centered" v-else>
                    <div class="column is-6">
                        <Notification class="is-info">
                            <span class="icon">
                                <i class="fa fa-info-circle" />
                            </span>
                            <span>
                                {{ translate("common.no_results_for_provided_criteria") }}
                            </span>
                        </Notification>
                    </div>
                </div>
            </template>
        </div>
    </section>

    <CompetitorResultsOptionsModal :event-id="eventId" ref="competitorResultsOptionsModal" @changed="onOptionsChanged" />

    <AdminCategorySidebar
        :event-id="eventId"
        ref="categorySidebar"
        :event-timezone="eventTimezone"
        :current-user-id="currentUserId"
        :can-view-roster="canManageResults"
    />
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, nextTick, watch } from "vue";
    import Checkbox2 from "../Common/Checkbox2.vue";
    import store from "../Common/store";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import BracketTypeBadge from "../Common/BracketTypeBadge.vue";
    import QueryInput from "../Common/QueryInput.vue";
    import objectMatcher from "../Common/objectMatcher";
    import Notification from "../Common/Notification.vue";
    import { translate } from "../Common/translations";
    import { successToast } from "../Common/toasts";
    import { handleError } from "../Common/errorHandler";
    import urls from "../Urls/urls";
    import history from "../Common/history";
    import { convertToTimezone } from "../Common/dateTimeUtils";
    import competitorResultsApi, { CategoryPodium, ResultsReportOptions } from "../Api/competitorResultsApi";
    import eventSettingsApi from "../Api/eventSettingsApi";
    import KataResultsTable from "./BracketType/KataResultsTable.vue";
    import StandardResultsTable from "./BracketType/StandardResultsTable.vue";
    import { BracketTypeEnum } from "../Common/types";
    import CompetitorResultsOptionsOverview from "./CompetitorResultsOptions/CompetitorResultsOptionsOverview.vue";
    import { useCompetitorResultsOptionsStore } from "./CompetitorResultsOptions/competitorResultsOptionsStore";
    import CompetitorResultsOptionsModal from "./CompetitorResultsOptions/CompetitorResultsOptionsModal.vue";
    import { eventBus, OPEN_COMPETITOR_RESULTS_OPTIONS_MODAL } from "./eventBus";
    import { ComponentExposed } from "vue-component-type-helpers";
    import AdminCategorySidebar from "../Common/Fights/AdminCategorySidebar.vue";
    import { useFeatureAnnouncement } from "../Common/FeatureAnnouncements/useFeatureAnnouncement";

    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
        currentUserId?: number | null;
        canManageResults: boolean;
    }>();

    const optionsStore = useCompetitorResultsOptionsStore();

    const initDone = ref(false);
    const categoryPodiums = ref<CategoryPodium[]>([]);
    const showDecorated = ref(false);
    const sharingEnabled = ref<boolean>(false);

    const historyInstance = history.getInstance(window);
    const searchQuery = ref(historyInstance.getQueryParam("q") || "");

    watch(searchQuery, newValue => {
        historyInstance.setQueryParam("q", newValue || null);
    });
    const requestsInProgress = ref(0);

    function getUrlToDownloadCsv() {
        return urls.eventAdmin().competitorResultsCsv(props.eventId, getRequestOptions());
    }

    const getShowDecoratedStoreKey = eventId => `showDecoratedCategories_${eventId}`;

    const filteredCategoryPodiums = computed(() => {
        return categoryPodiums.value.filter(categoryPodium => {
            if (showDecorated.value !== categoryPodium.decorated) {
                return false;
            }

            const categoryPodiumAsText = categoryPodium.category + " " + JSON.stringify(categoryPodium.competitors);
            return objectMatcher.matchString(searchQuery.value, categoryPodiumAsText);
        });
    });

    const totalCategories = computed(() => categoryPodiums.value.length);
    const totalDecorated = computed(() => categoryPodiums.value.filter(categoryPodium => categoryPodium.decorated).length);

    function onShowDecoratedChange() {
        store.setBoolean(getShowDecoratedStoreKey(props.eventId), showDecorated.value);
    }

    function refreshResults() {
        requestsInProgress.value++;
        return fetchCategoryPodiums()
            .catch(handleError)
            .finally(() => {
                requestsInProgress.value--;
            });
    }

    const competitorResultsOptionsOverview = ref<InstanceType<typeof CompetitorResultsOptionsOverview> | null>(null);

    function getRequestOptions(): ResultsReportOptions {
        return optionsStore.resultOptions;
    }

    function fetchCategoryPodiums() {
        return competitorResultsApi.fetchCategoryPodiums(props.eventId, getRequestOptions()).then(categoryPodiumsResponse => {
            categoryPodiums.value = categoryPodiumsResponse;
        });
    }

    const categorySidebar = ref<ComponentExposed<typeof AdminCategorySidebar> | null>(null);

    function openBracketSidebar(parameterId: number) {
        categorySidebar.value?.open(parameterId);
    }

    function changeSharingStatus() {
        const newStatus = !sharingEnabled.value;

        return eventSettingsApi
            .updateSetting(props.eventId, `results-public-access`, newStatus)
            .then(() => {
                return fetchSharingStatus();
            })
            .catch(handleError);
    }

    function fetchSharingStatus() {
        return eventSettingsApi.getSingleSetting(props.eventId, `results-public-access`).then(setting => {
            sharingEnabled.value = setting.value as boolean;
        });
    }

    function saveDecoration(parameterId) {
        return competitorResultsApi
            .updateDecoration(props.eventId, parameterId, true)
            .then(() => {
                successToast(translate("admin_results.category_decorated"));
                return refreshResults();
            })
            .catch(handleError);
    }

    function removeDecoration(parameterId) {
        return competitorResultsApi
            .updateDecoration(props.eventId, parameterId, false)
            .then(() => {
                return refreshResults();
            })
            .catch(handleError);
    }

    onMounted(() => {
        showDecorated.value = store.getBoolean(getShowDecoratedStoreKey(props.eventId));

        return optionsStore
            .loadResultsReportOptions(props.eventId)
            .then(() => {
                // Sharing status comes from the generic settings endpoint, which stays broad-only.
                return Promise.all([refreshResults(), props.canManageResults ? fetchSharingStatus() : Promise.resolve()]);
            })
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
                announceBracketSidebarIfEligible();
            });
    });

    function announceBracketSidebarIfEligible() {
        if (!props.currentUserId || filteredCategoryPodiums.value.length === 0) {
            return;
        }

        nextTick(() => {
            useFeatureAnnouncement().showEligible(props.currentUserId as number, [
                {
                    key: "competitor_results_bracket_sidebar",
                    element: "#competitor-results-first-category",
                },
            ]);
        });
    }

    // OPTIONS MODAL

    const competitorResultsOptionsModal = ref<ComponentExposed<typeof CompetitorResultsOptionsModal> | null>(null);

    function onOptionsChanged() {
        return refreshResults();
    }

    onMounted(() => {
        eventBus.$on(OPEN_COMPETITOR_RESULTS_OPTIONS_MODAL, () => {
            competitorResultsOptionsModal.value?.open(optionsStore.resultOptions);
        });
    });
</script>

<style scoped>
    .evenly-distributed {
        justify-content: space-between;
        align-items: center;
    }

    .is-title-bar {
        margin-bottom: 0;
    }

    .category-name.title {
        margin-bottom: 0;
    }

    .is-title-bar .finish-time {
        font-size: 0.85rem;
        margin-left: 1rem;
        margin-right: 1rem;
    }

    .category-container {
        margin-bottom: 1.5rem;
    }
</style>
